import type { RendererProps } from "../core/element-registry"
import type { CogoElement } from "../core/types"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardAction,
  CardContent,
  CardFooter,
} from "../components/ui/card"
import { cn } from "../lib/utils"
import { stripPresetConflicts } from "../lib/preset-utils"
import { spacingToken } from "../lib/design-tokens"

export function CardRenderer({ element }: RendererProps) {
  const { dispatchEvent, itemContext, resolveSymbol } = useRenderContext()
  // Theme-v2 atom defaults feed the fallback chain:
  //   element.variant/size → atomDefaults.default* → shadcn "default"
  const atomDefaults = useAtomDefaults("card")
  const resolvedVariant = element.variant ?? atomDefaults.defaultVariant ?? "default"
  const resolvedSize = (element.size as string | undefined)
    ?? atomDefaults.defaultSize
    ?? "default"
  const atomDataAttrs = {
    "data-atom-type": "card" as const,
    "data-variant": resolvedVariant,
    "data-size": resolvedSize,
  }
  const containerVariant = element.variant ?? ""

  const variantClasses: Record<string, string> = {
    card: "overflow-hidden transition-shadow hover:shadow-lg",
    default: "",
  }

  // Resolve tap event → onClick (same pattern as container-renderer)
  const tapHandler = element.events?.tap
  const isNavigateAction = typeof tapHandler === "object" && tapHandler.action === "navigate" && tapHandler.params
  const navigateRoute = isNavigateAction
    ? String(resolveSymbol(tapHandler.params!.route as string) ?? "")
    : undefined

  const handleClick = tapHandler
    ? (e: React.MouseEvent) => {
        if (isNavigateAction && navigateRoute) {
          e.preventDefault()
          dispatchEvent(element.id, "tap", { __resolvedRoute: navigateRoute, ...itemContext })
        } else {
          dispatchEvent(element.id, "tap", itemContext)
        }
      }
    : (() => {
        // No inline events, but check if there's an event binding via events.cogo.json
        // Dispatch tap so cogo-app can resolve bindings
        return (e: React.MouseEvent) => {
          dispatchEvent(element.id, "tap", itemContext)
        }
      })()

  // Helper: render a card child based on its semantic type. liftV4Props
  // mirrors normalizeElement's first pass so v4 schema (`{props:{text}}`)
  // surfaces `child.text` for the slot-text branches below.
  function renderCardChild(rawChild: CogoElement) {
    const child = liftV4Props(rawChild)
    switch (child.type) {
      case "card-header":
        return (
          <CardHeader key={child.id} className={child.className}>
            {child.children?.map((c) => renderCardChild(c))}
          </CardHeader>
        )
      case "card-title": {
        const resolvedText = child.text ? String(resolveSymbol(child.text) ?? child.text) : undefined
        // Phase 5703 (v64.2): forward semantic heading tag from JSON to CardTitle.
        // Defaults to "div" when unset — preserves existing behavior.
        const headingTag = (child as { tag?: string }).tag as
          | "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | undefined
        return (
          <CardTitle key={child.id} className={child.className} tag={headingTag}>
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardTitle>
        )
      }
      case "card-description": {
        const resolvedText = child.text ? String(resolveSymbol(child.text) ?? child.text) : undefined
        return (
          <CardDescription key={child.id} className={child.className}>
            {resolvedText}
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardDescription>
        )
      }
      case "card-action":
        return (
          <CardAction key={child.id} className={child.className}>
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardAction>
        )
      case "card-content":
        return (
          <CardContent key={child.id} className={child.className}>
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardContent>
        )
      case "card-footer":
        return (
          <CardFooter key={child.id} className={child.className}>
            {child.children?.map((c) => (
              <ElementRenderer key={c.id} element={c} />
            ))}
          </CardFooter>
        )
      default:
        // Fallback: render non-compound children via ElementRenderer
        return <ElementRenderer key={child.id} element={child} />
    }
  }

  // Render children using compound-aware helper
  const renderChildren = () =>
    element.children?.map((child) => renderCardChild(child))

  // Render as <a> for navigate actions
  if (isNavigateAction && navigateRoute) {
    return (
      <a
        href={navigateRoute}
        className={cn(variantClasses[containerVariant] ?? "", stripPresetConflicts(element.className), "cursor-pointer")}
        onClick={handleClick}
        {...atomDataAttrs}
      >
        {element.padding ? (
          <div className={cn(`p-${spacingToken(element.padding)}`)}>
            {renderChildren()}
          </div>
        ) : (
          <>
            {renderChildren()}
          </>
        )}
      </a>
    )
  }

  return (
    <Card
      className={cn(variantClasses[containerVariant] ?? "", stripPresetConflicts(element.className), "cursor-pointer")}
      size={(element.size as "default" | "sm") ?? "default"}
      onClick={handleClick}
      {...atomDataAttrs}
    >
      {element.padding ? (
        <div className={cn(`p-${spacingToken(element.padding)}`)}>
          {renderChildren()}
        </div>
      ) : (
        <>
          {renderChildren()}
        </>
      )}
    </Card>
  )
}
